import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ImageWatermarkRemover = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/image-watermark-remover',
			name: 'Image Watermark Remover — Remove Watermark from Pictures Online | Lift',
			description:
				"Remove watermarks from photos in one step — Lift's AI finds logos, text overlays, and stamps on any image, erases them cleanly, and keeps the rest of the shot intact. Try Lift now!",
			url: 'https://liftapp.ai/image-watermark-remover',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/image-watermark-remover#product' },
				{ '@id': 'https://liftapp.ai/image-watermark-remover#howto' },
				{ '@id': 'https://liftapp.ai/image-watermark-remover#benefits' },
				{ '@id': 'https://liftapp.ai/image-watermark-remover#faq' },
				{ '@id': 'https://liftapp.ai/image-watermark-remover#why-lift' },
				{ '@id': 'https://liftapp.ai/image-watermark-remover#explore-tools' },
				{ '@id': 'https://liftapp.ai/image-watermark-remover#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/image-watermark-remover#product',
			name: 'Image Watermark Remover',
			description:
				"Erase watermarks, logos, and stamps from photos with Lift's AI. Upload a picture, the AI finds every mark on its own and rebuilds the background behind it — no brushing, no masking.",
			url: 'https://liftapp.ai/image-watermark-remover',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/image-watermark-remover#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Image',
					text: 'Drop in a photo from your device — all common image formats are supported.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let AI Detect And Remove',
					text: 'Removal starts automatically: Lift locates every watermark, logo, or stamp and rebuilds the background behind it.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Review The Result',
					text: 'Drag the before-and-after slider to compare, step back with Undo if needed, and tap Retry for a fresh pass.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download Your Result',
					text: 'Save the clean image to your device in its original format, ready to post or share.',
				},
			],
		},
		// benefits
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-watermark-remover#benefits',
			name: 'Who can benefit from Image Watermark Remover?',
			description:
				"Lift's Image Watermark Remover helps content creators, online sellers, photographers, designers, and everyday users clear logos, stamps, and overlays from any photo in one pass.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Old app logos and stamps on your own photos make a feed look inconsistent. Clear them before posting so every post, story, and thumbnail looks polished.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Online sellers and marketers — Product shots from supplier catalogs often arrive with a logo in the corner. Lift removes it in one pass, leaving clean images ready for listings, ads, and campaigns.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Photographers — Proofs go out with a studio mark, but clients need the final version clean. Skip manual cloning and let AI restore the background behind the mark in seconds.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Designers — Review copies and drafts usually carry an approval stamp. Once the version is signed off, Lift clears the mark so the picture drops straight into the layout.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Everyday users — A date stamp or a camera app logo can spoil a great memory. Wipe it away from any photo and keep the moment exactly as you remember it.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Image Watermark Remover by Lift',
				description:
					"Photo with a logo next to the same photo after Lift's watermark remover from image cleaned it.",
			},
		},
		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/image-watermark-remover#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Image Watermark Remover?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Image Watermark Remover is Lift's AI tool for deleting watermarks, logos, and stamps from photos. You upload a picture, the AI detects every mark on its own, removes it, and reconstructs the background so the result looks as if the watermark was never there.",
					},
				},
				{
					'@type': 'Question',
					name: 'How do I remove a watermark from a photo?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your image to Lift and the removal begins automatically — there is nothing to select or outline. When the AI finishes, compare the result with the slider, tap Retry if you want another pass, and download the clean version.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will the quality of my photo stay the same?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift only rebuilds the area under the watermark and leaves the rest of the picture untouched, so colors, sharpness, and detail stay exactly as they were. The AI fills the cleared spot with matching texture and lighting, so there are no smudges or patches.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can Lift remove more than one watermark from the same photo?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. The AI scans the whole image and picks up every watermark, logo, or stamp it finds — not just the largest one. All of them are removed in a single pass, and the background behind each is rebuilt separately so the result stays seamless.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your photos securely and uses them only to generate your results.',
					},
				},
			],
		},
		// why-lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-watermark-remover#why-lift',
			name: 'Why Lift Image Watermark Remover',
			description:
				'Discover the advantages of Lift Image Watermark Remover: high-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyLift.7c19de84.webp',
				name: 'Why use Lift Image Watermark Remover',
				description:
					"Clean photo result after using Lift's AI to remove a watermark from an image.",
			},
		},
		// explore-tools
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-watermark-remover#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		// all-in-one
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/image-watermark-remover#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Image Watermark Remover',
					item: 'https://liftapp.ai/image-watermark-remover',
				},
			],
		},
	],
};
